import {Injectable} from '@angular/core';
import {L10nTranslationService} from 'angular-l10n';
import Decimal from 'decimal.js';
import * as _ from 'lodash-es';
import moment from 'moment';
import {createDisplaySymbol, DisplaySymbol} from '../../common/display-symbol';
import {formatDateTime} from '../../common/formatters';
import {AccountService} from '../../components/accounts';
import {InvPortfolioService} from '../../components/investment-portfolio';
import {
    getOrderQtyCurrencySuffix,
    getOrderQtyFormats,
    getOrderQtyMultiCurrencyInfoForOrder,
    getQtyFromOrder,
    OrderQtyMultiCurrencyInfo
} from '../../components/order-qty-multi-currency';
import {isAsecurity} from '../../components/security/aspreader-security';
import {isSmartSecurity} from '../../components/smart-security';
import {
    DisplayDecimal,
    DisplayDecimalWithOptCurrency,
    formatPrice,
    formatQuantity,
    formatReport,
    SymbolDecimalsService,
    SymbolFormats,
    USE_QTY_STORE_FORMAT
} from '../../components/symbol-decimals';
import {AccountType} from '../../enums/account-type';
import {AssetType} from '../../enums/asset-type';
import {Exchange, EXCHANGE_NAMING} from '../../enums/exchange';
import {
    isAlgoOrderWithChildren,
    isOrderChildOfComplexAlgo,
    ORDER_ALGO_TYPE_NAMING,
    parseAlgoTypeFromOrder
} from '../../enums/order-algo-type';
import {ORDER_REJECT_REASON_NAMING} from '../../enums/order-rejection-reason';
import {OrderStatus} from '../../enums/order-status';
import {Side} from '../../enums/side';
import {DisplayExchangeAccountFactory} from '../../factories/display-exchange-account.factory';
import {Order} from '../../models/order';
import {User} from '../../models/user';

export interface OrderStatusInfo {
    key: OrderStatus;
    icon: string;
    label: string;
    dontTranslateLabel?: boolean;
}

export const ORDER_STATUS_INFO_DICT: {[orderStatus in OrderStatus]: OrderStatusInfo} = {
    [OrderStatus.ACTIVE]: {
        key: OrderStatus.ACTIVE,
        icon: 'active',
        label: 'enums.order-status.active'
    },
    [OrderStatus.CANCEL]: {
        key: OrderStatus.CANCEL,
        icon: 'canceled',
        label: 'enums.order-status.cancel'
    },
    [OrderStatus.CANCELED]: {
        key: OrderStatus.CANCELED,
        icon: 'canceled',
        label: 'enums.order-status.canceled'
    },
    [OrderStatus.EXPIRED]: {
        key: OrderStatus.EXPIRED,
        icon: 'canceled',
        label: 'enums.order-status.expired'
    },
    [OrderStatus.FILLED]: {
        key: OrderStatus.FILLED,
        icon: 'filled',
        label: 'enums.order-status.filled'
    },
    [OrderStatus.PARTIALLY_FILLED]: {
        key: OrderStatus.PARTIALLY_FILLED,
        icon: 'partially-filled',
        label: 'enums.order-status.partially_filled'
    },
    [OrderStatus.REPLACED]: {
        key: OrderStatus.REPLACED,
        icon: 'rejected',
        label: 'enums.order-status.replaced'
    },
    [OrderStatus.REJECTED]: {
        key: OrderStatus.REJECTED,
        icon: 'rejected',
        label: 'enums.order-status.rejected'
    },
    [OrderStatus.WAITING_ACTIVATION]: {
        key: OrderStatus.WAITING_ACTIVATION,
        icon: 'active',
        label: 'enums.order-status.waiting_activation'
    },
    [OrderStatus.UNKNOWN]: {
        key: OrderStatus.UNKNOWN,
        icon: 'rejected',
        label: 'enums.order-status.unknown'
    }
};

const createNotDesignedOrderStatusInfo = (notDesignedOrderStatus: OrderStatus): OrderStatusInfo => ({
    key: notDesignedOrderStatus,
    icon: undefined,
    label: notDesignedOrderStatus || '',
    dontTranslateLabel: true
});

type ValueFormatter = (value: number) => DisplayDecimal<string>;

/**
 * [TRAD-115] Exec. Qty with supporting different currencies.
 */
export interface ExecQtyWithMultiCurrency {
    qtyAssetType: AssetType;
    isQtyInBaseQtyAsset: boolean;
    leftSideValue: DisplayDecimalWithOptCurrency<string>;
    rightSideValue: DisplayDecimalWithOptCurrency<string>;
    asString: string; // Example: "1 / 10 ETH", "1 ETH / 10 EUR"
    asStringOptRightSideCurrency: string; // Example: "1 / 10", "1 ETH / 10 EUR"
    progressPercent: number;
    isFull: boolean;
}

export interface DisplayOrder {
    order: Order;

    displaySymbol: DisplaySymbol;
    qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo;
    orderId: string;
    clOrdId: string;
    origClOrdId: string;
    ordType: string;
    algoType: string;
    isParentWithChildren: boolean;
    isChildOfComplexAlgo: boolean;
    companyId: string | number;
    companyName: string;
    accountId: string | number;
    accountName: string;
    accountBackofficeName: string;
    userId: string;
    userLogin: string;
    userName: string;
    userEmail: string;
    execQty: string;
    execQtyWithMultiCurrency: ExecQtyWithMultiCurrency;
    leftQty: DisplayDecimal;
    side: string;
    createdOn: string;
    type: string;
    limitPrice: DisplayDecimal;
    avgPrice: DisplayDecimal;
    stopPrice: DisplayDecimal;
    status: OrderStatusInfo;
    qtyCurrency: string;

    sideClasses: string;

    transactTimestamp: number; // Used for sorting.

    portfolio: string;
    invEntity: string;
}

const BUY_SIDE_CLASS = '-buy-side';
const SELL_SIDE_CLASS = '-sell-side';

@Injectable()
export class DisplayOrderFactory {

    constructor(
        private symbolDecimalsService: SymbolDecimalsService,
        private accountService: AccountService,
        private invPortfolioService: InvPortfolioService,
        private translationService: L10nTranslationService
    ) {}

    public createDisplayOrders(orders: Order[]): DisplayOrder[] {
        return orders.map(order => this.createDisplayOrder(order));
    }

    // tslint:disable:cyclomatic-complexity
    public createDisplayOrder(order: Order): DisplayOrder {
        const {security} = order;
        const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfoForOrder(order);
        const symbolFormats: SymbolFormats = this.symbolDecimalsService.getSecurityFormats(security);
        const priceFormatter: ValueFormatter = value => formatPrice(value, symbolFormats);
        const reportFormatter: ValueFormatter = value => formatReport(value, symbolFormats);

        const preciseQuantityFormatter: ValueFormatter = value =>
            formatQuantity(value, symbolFormats, USE_QTY_STORE_FORMAT);

        const formattedExecutedQtyStr: string = order.state && preciseQuantityFormatter(
            order.state.cumQty).value || '0';

        const formattedOrderQtyStr: string = preciseQuantityFormatter(order.orderQty).value;

        const formattedLeftQty: DisplayDecimal = preciseQuantityFormatter(
            order.orderQty - (order.state && order.state.cumQty || 0)
        );

        const user: User = order.initiator;

        return {
            order,

            displaySymbol: createDisplaySymbol(security),
            qtyMultiCurrencyInfo,
            orderId:       String(order.orderId),
            clOrdId:       order.clOrdId,
            origClOrdId:   order.origClOrdId,
            ordType:       _.upperCase(order.ordType),
            algoType:      ORDER_ALGO_TYPE_NAMING[parseAlgoTypeFromOrder(order.algoType)] || '',

            isParentWithChildren: isAlgoOrderWithChildren(order),
            isChildOfComplexAlgo: isOrderChildOfComplexAlgo(order),

            companyId:   order.company && order.company.id || '',
            companyName: order.company && order.company.name || '',
            userId:      user && String(user.id) || '',
            userLogin:   user && user.login || '',
            userName:    user && user.fullName || '',
            userEmail:   user && user.email || '',
            execQty:     `${formattedExecutedQtyStr} / ${formattedOrderQtyStr}`,

            execQtyWithMultiCurrency: this.getExecQtyWithMultiCurrency(order, qtyMultiCurrencyInfo, symbolFormats),

            leftQty:     formattedLeftQty,
            side:        _.upperCase(order.side),
            createdOn:   formatDateTime(order.transactTime),
            type:        _.upperCase(order.transactionType),
            limitPrice:  priceFormatter(order.price),
            avgPrice:    reportFormatter(order.state && order.state.avgPx),
            stopPrice:   priceFormatter(order.stopPx),
            status:      order.state && ORDER_STATUS_INFO_DICT[order.state.orderStatus]
                             || createNotDesignedOrderStatusInfo(order.state.orderStatus),
            sideClasses: order.side === Side.BUY ? BUY_SIDE_CLASS : SELL_SIDE_CLASS,
            qtyCurrency: getOrderQtyCurrencySuffix(security, qtyMultiCurrencyInfo),

            transactTimestamp: moment(order.transactTime).valueOf(),

            ...this.getDisplayOrderAccountInfo(order),
            ...this.invPortfolioService.getDisplayInvPortfolio(order)
        };
    }

    /**
     * [TRAD-115] Formats exec. qty with supporting different currency
     * in the left and right sides.
     * Examples:
     * the same currency: 0 / 1.2 LTC
     * diff. currencies: 1.2 LTC / 1,234 EUR
     */
    public getExecQtyWithMultiCurrency(
        order: Order,
        qtyMultiCurrencyInfo: OrderQtyMultiCurrencyInfo,
        securityFormats: SymbolFormats
    ): ExecQtyWithMultiCurrency {
        const {security} = order;

        const qtyFormats = getOrderQtyFormats({
            security,
            securityFormats,
            securityInputParams: this.symbolDecimalsService.getSecurityInputParams(security),
            qtyMultiCurrencyInfo,
            orderOrReport:       order
        });

        const {isQtyInBaseQtyAsset} = qtyFormats;

        const cumQtyInBaseQtyAsset: number = order.state && order.state.cumQty || 0;
        const qtyAssetQty: number = getQtyFromOrder(security, order, qtyMultiCurrencyInfo);

        const avgPx = (order.state && order.state.avgPx || 0);
        const currentProgressValue = isQtyInBaseQtyAsset
            ? cumQtyInBaseQtyAsset
            : Decimal.mul(cumQtyInBaseQtyAsset, avgPx).toNumber();

        const isFull = order.state && order.state.orderStatus === OrderStatus.FILLED;

        const progressPercent = isFull
            ? 100
            : Math.min(100, Decimal.div(currentProgressValue, qtyAssetQty).mul(100).toNumber());

        const result: ExecQtyWithMultiCurrency = {
            qtyAssetType:   qtyFormats.qtyAssetType,
            leftSideValue:  formatQuantity(cumQtyInBaseQtyAsset, qtyFormats.baseFormats, USE_QTY_STORE_FORMAT),
            rightSideValue: formatQuantity(qtyAssetQty, qtyFormats.formats, USE_QTY_STORE_FORMAT),

            asString:                     undefined, // To be set below.
            asStringOptRightSideCurrency: undefined, // To be set below.

            progressPercent,
            isFull,
            isQtyInBaseQtyAsset
        };

        if (!isQtyInBaseQtyAsset) {
            if (result.leftSideValue.originalValue) {
                result.leftSideValue.currencySymbol = qtyFormats.baseCurrencySymbol;
            }

            result.rightSideValue.currencySymbol = qtyFormats.currencySymbol;
        } else {
            result.rightSideValue.currencySymbol = qtyFormats.baseCurrencySymbol;
        }

        const commonStringParts: string[] = [
            result.leftSideValue.value,
            result.leftSideValue.currencySymbol,
            '/',
            result.rightSideValue.value
        ];

        result.asString = [
            ...commonStringParts,
            result.rightSideValue.currencySymbol
        ].filter(Boolean).join(' ');

        result.asStringOptRightSideCurrency = [
            ...commonStringParts,
            !isQtyInBaseQtyAsset && result.rightSideValue.currencySymbol
        ].filter(Boolean).join(' ');

        return result;
    }

    public getDisplayRejectionReasonString(order: Order): string {
        const knownReasonNaming: string = ORDER_REJECT_REASON_NAMING[order.state.ordRejReason];

        return [
            knownReasonNaming && this.translationService.translate(knownReasonNaming),
            order.state.ordRejReasonText
        ]
            .filter(part => !!part)
            .join(': ');
    }

    private getDisplayOrderAccountInfo(
        order: Order
    ): Pick<DisplayOrder, 'accountId' | 'accountName' | 'accountBackofficeName'> {
        const {security} = order;

        const isSmart = isSmartSecurity(security);
        const isAutospreader = isAsecurity(security);

        const accountId = isAutospreader
            ? undefined
            : order.accountId;

        const account = (isAutospreader ? undefined : order.account)
            || (accountId ? this.accountService.getAccountByIdFromCache(accountId) : undefined);

        const accountName = isSmart
            ? EXCHANGE_NAMING[Exchange.SMART]
            : isAutospreader
                ? EXCHANGE_NAMING[Exchange.AUTOSPREADER]
                : (
                    account && DisplayExchangeAccountFactory.getDisplayAccountName(
                        account,
                        true,
                        order.leverage ? AccountType.MARGIN : AccountType.CASH,
                        this.translationService
                    )
                );

        return {
            accountId,
            accountName: accountName || '',

            accountBackofficeName: accountName || ''
        };
    }

}
